import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, LogOut, Search, Globe } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from 'react-i18next';
import Logo from "./Logo";

const navigation = [
  { name: "Home", href: "/" },
  { name: "Jobs", href: "/jobs" },
  { name: "About", href: "/about" },
  { name: "Contact", href: "/contact" },
  { name: "Onboarding", href: "/onboarding" },
];

const Header = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const isAdmin = currentUser?.role === 'admin';

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const toggleLanguage = () => {
    const currentLang = i18n.language || 'en';
    const newLang = currentLang.startsWith('en') ? 'es' : 'en';
    i18n.changeLanguage(newLang).then(() => {
      localStorage.setItem('i18nextLng', newLang);
    });
  };

  const getCurrentLanguage = () => {
    const currentLang = i18n.language || 'en';
    return currentLang.startsWith('en') ? t('language.english') : t('language.spanish');
  };

  return (
    <React.Fragment>
      {/* Top Bar - Hidden on Mobile and Tablet */}
      <div className="hidden lg:block bg-accent-light text-gray-700 py-2">
        <div className="container mx-auto flex justify-between items-center px-4">
          <div className="flex space-x-4">
            <Link
              to="/home"
              className={`font-medium ${
                location.pathname === "/home"
                  ? "text-accent"
                  : "hover:text-accent"
              }`}
            >
              {t('navigation.forJobSeekers')}
            </Link>
            <Link
              to="/employers"
              className={`font-medium ${
                location.pathname === "/employers"
                  ? "text-accent"
                  : "hover:text-accent"
              }`}
            >
              {t('navigation.forEmployers')}
            </Link>
          </div>
          <div className="flex items-center space-x-6">
            {currentUser && (
              <Link to="/employee-portal" className="hover:text-accent">
                {t('navigation.employeePortal')}
              </Link>
            )}
            {isAdmin && (
              <Link to="/admin-dashboard" className="hover:text-accent">
                {t('navigation.adminDashboard')}
              </Link>
            )}
            {currentUser ? (
              <button
                onClick={handleLogout}
                className="flex items-center hover:text-accent"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('auth.logout')}
              </button>
            ) : (
              <Link to="/login" className="hover:text-accent">
                {t('auth.login')}
              </Link>
            )}
            <button 
              onClick={toggleLanguage}
              className="flex items-center hover:text-accent"
            >
              <Globe className="w-4 h-4 mr-2" />
              {getCurrentLanguage()}
              <ChevronDown className="w-4 h-4 ml-1" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-primary text-white">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex-shrink-0">
              <Logo className="h-8 w-auto text-white" />
            </Link>

            {/* Desktop Navigation - Only show on large screens */}
            <nav className="hidden lg:flex items-center space-x-6">
              <Link to="/jobs" className="font-medium hover:text-accent">
                {t('navigation.findAJob')}
              </Link>
              <Link to="/announcements" className="font-medium hover:text-accent">
                {t('navigation.announcements')}
              </Link>
              {/* <Link to="/blog" className="font-medium hover:text-accent">
                Blog
              </Link> */}
              <Link 
                to="/onboarding" 
                className="font-medium hover:text-accent"
              >
                Onboarding Forms
              </Link>
              <div className="relative group">
                <button className="flex items-center font-medium hover:text-accent">
                  {t('navigation.workingWithSBG')}
                  <ChevronDown className="ml-1 w-4 h-4" />
                </button>
                <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 transform transition-all duration-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible">
                  <Link
                    to="/about"
                    className="block px-4 py-2 text-gray-800 hover:bg-primary-light transition-colors"
                  >
                    {t('navigation.aboutUs')}
                  </Link>
                  <Link
                    to="/services"
                    className="block px-4 py-2 text-gray-800 hover:bg-primary-light transition-colors"
                  >
                    {t('navigation.ourServices')}
                  </Link>
                  <Link
                    to="/team"
                    className="block px-4 py-2 text-gray-800 hover:bg-primary-light transition-colors"
                  >
                    {t('navigation.ourTeam')}
                  </Link>
                  <Link
                    to="/customer-service"
                    className="block px-4 py-2 text-gray-800 hover:bg-primary-light transition-colors"
                  >
                    {t('navigation.customerService')}
                  </Link>
                  <Link
                    to="/blog"
                    className="block px-4 py-2 text-gray-800 hover:bg-primary-light transition-colors"
                  >
                    Blog
                  </Link>
                </div>
              </div>
              <Link
                to="/general-application"
                className="font-medium hover:text-accent"
              >
                {t('navigation.applyNow')}
              </Link>
              <Link to="/contact" className="font-medium hover:text-accent">
                {t('navigation.contact')}
              </Link>
              <button className="text-white hover:text-accent">
                <Search className="w-5 h-5" />
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="lg:hidden text-white hover:text-accent focus:outline-none p-2 rounded-md transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            {/* Dark overlay behind the menu */}
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={toggleMenu}></div>
            
            {/* Menu panel */}
            <div className="absolute right-0 top-0 bottom-0 w-4/5 max-w-sm bg-white shadow-xl overflow-y-auto">
              <div className="flex flex-col h-full">
                {/* Mobile Header */}
                <div className="flex justify-between items-center p-4 bg-primary sticky top-0 z-10">
                  <Logo className="h-8 w-auto text-white" />
                  <button
                    onClick={toggleMenu}
                    className="text-white hover:text-accent focus:outline-none p-2 rounded-full hover:bg-primary-dark"
                    aria-label="Close menu"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
                <nav className="px-6 py-4 overflow-y-auto">
                  {/* User and Admin Links */}
                  <div className="mb-6 pb-4 border-b border-gray-200">
                    {currentUser && (
                      <div className="flex items-center mb-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {currentUser?.email || t('common.anonymous')}
                          </p>
                        </div>
                      </div>
                    )}
                    {/* Add For Job Seekers and For Employers links */}
                    <Link
                      to="/home"
                      className="block py-3 text-gray-800 hover:text-primary font-medium border-b border-gray-100"
                      onClick={toggleMenu}
                    >
                      {t('navigation.forJobSeekers')}
                    </Link>
                    <Link
                      to="/employers"
                      className="block py-3 text-gray-800 hover:text-primary font-medium border-b border-gray-100"
                      onClick={toggleMenu}
                    >
                      {t('navigation.forEmployers')}
                    </Link>
                    {currentUser && (
                      <>
                        <Link
                          to="/employee-portal"
                          className="block py-3 text-gray-800 hover:text-primary font-medium border-b border-gray-100"
                          onClick={toggleMenu}
                        >
                          {t('navigation.employeePortal')}
                        </Link>
                      </>
                    )}
                    {isAdmin && (
                      <>
                        <Link
                          to="/admin-dashboard"
                          className="block py-3 text-gray-800 hover:text-primary font-medium border-b border-gray-100"
                          onClick={toggleMenu}
                        >
                          {t('navigation.adminDashboard')}
                        </Link>
                      </>
                    )}
                    {currentUser ? (
                      <button
                        onClick={() => {
                          handleLogout();
                          toggleMenu();
                        }}
                        className="block py-3 text-gray-800 hover:text-primary w-full text-left font-medium border-b border-gray-100"
                      >
                        {t('auth.logout')}
                      </button>
                    ) : (
                      <Link
                        to="/login"
                        className="block py-3 text-gray-800 hover:text-primary font-medium border-b border-gray-100"
                        onClick={toggleMenu}
                      >
                        {t('auth.login')}
                      </Link>
                    )}
                  </div>

                  {/* Main Navigation for Mobile */}
                  <div className="space-y-0">
                    <Link
                      to="/jobs"
                      className="block py-3 text-gray-800 hover:text-primary border-b border-gray-100 font-medium"
                      onClick={toggleMenu}
                    >
                      {t('navigation.findAJob')}
                    </Link>
                    <Link
                      to="/announcements"
                      className="block py-3 text-gray-800 hover:text-primary border-b border-gray-100 font-medium"
                      onClick={toggleMenu}
                    >
                      {t('navigation.announcements')}
                    </Link>
                    <Link
                      to="/about"
                      className="block py-3 text-gray-800 hover:text-primary border-b border-gray-100 font-medium"
                      onClick={toggleMenu}
                    >
                      {t('navigation.aboutUs')}
                    </Link>
                    <Link
                      to="/services"
                      className="block py-3 text-gray-800 hover:text-primary border-b border-gray-100 font-medium"
                      onClick={toggleMenu}
                    >
                      {t('navigation.ourServices')}
                    </Link>
                    <Link
                      to="/team"
                      className="block py-3 text-gray-800 hover:text-primary border-b border-gray-100 font-medium"
                      onClick={toggleMenu}
                    >
                      {t('navigation.ourTeam')}
                    </Link>
                    <Link
                      to="/customer-service"
                      className="block py-3 text-gray-800 hover:text-primary border-b border-gray-100 font-medium"
                      onClick={toggleMenu}
                    >
                      {t('navigation.customerService')}
                    </Link>
                    <Link
                      to="/blog"
                      className="block py-3 text-gray-800 hover:text-primary border-b border-gray-100 font-medium"
                      onClick={toggleMenu}
                    >
                      Blog
                    </Link>
                    <Link
                      to="/general-application"
                      className="block py-3 text-gray-800 hover:text-primary border-b border-gray-100 font-medium"
                      onClick={toggleMenu}
                    >
                      {t('navigation.applyNow')}
                    </Link>
                    <Link
                      to="/contact"
                      className="block py-3 text-gray-800 hover:text-primary border-b border-gray-100 font-medium"
                      onClick={toggleMenu}
                    >
                      {t('navigation.contact')}
                    </Link>
                  </div>

                  {/* Language Selector for Mobile */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <button 
                      onClick={() => {
                        toggleLanguage();
                        toggleMenu();
                      }}
                      className="flex items-center text-gray-800 hover:text-primary w-full py-3 font-medium"
                    >
                      <Globe className="w-5 h-5 mr-3" />
                      {getCurrentLanguage()}
                      <ChevronDown className="w-4 h-4 ml-auto" />
                    </button>
                  </div>
                </nav>
              </div>
            </div>
          </div>
        )}
      </header>
    </React.Fragment>
  );
};

export default Header;
